import { useEffect, useMemo } from 'react'
import { useLogError } from '@/src/common/hooks/useLogError'
import {
  MortgageModuleDetailsSchema,
  MortgageModuleVariantName,
  useMortgageModuleParameterStore,
} from './useMortgageModuleDesignParameterStore'

const MODULE_FALLBACK_VALUES = {
  default: {
    title: 'Earn Mesa Points with Mesa Mortgage',
    card_name: 'The Mesa Homeowners Card',
    partner_name: 'Mesa Mortgage',
    description:
      'Get rewarded for your new mortgage or refinance and earn 1X Mesa Point per dollar on your loan. Get started by answering a few questions!.',
    action_text: 'Get Started',
    action_url: 'https://mesamortgage.my1003app.com/inquiry?time=1745954655175',
    disclosure_paragraphs: [],
    details_paragraphs: [
      'Get connected to a Mesa Loan Advisor to find the right financing for you. You can earn 1x Mesa Points on your loan amount.',
      'Submit your application for approval and work with our support team to get ready for closing.',
      "Whether you're looking for a new mortgage or refinancing options, Mesa Loan Advisors are here to help and get you rewarded.",
      'Terms and conditions apply.',
    ],
    image_url: undefined,
  },
  prmg: {
    title: 'Make Your Dream of Homeownership a Reality',
    card_name: 'The Mesa Homeowners Card for PRMG®',
    partner_name: 'PRMG®',
    description:
      'As a leading lender, Paramount Residential Mortgage Group has helped borrowers just like you purchase and refinance their dream homes.',
    action_text: 'Speak to your PRMG® Loan Officer',
    action_url: 'https://www.prmg.net/prmg-home-mortgages/',
    image_url: 'https://assets.mesamember.com/MesaMortgageDesigns/mesa-mortgage-prmg.png',
    details_paragraphs: [
      'As a leading lender, Paramount Residential Mortgage Group has helped borrowers just like you purchase and refinance their dream homes.',
      'Paramount Residential Mortgage Group has a deep understanding of the mortgage market. Having garnered thousands of 5-star reviews, our commitment to customer satisfaction is second to none. PRMG knows that every borrower’s needs are unique and, as such, offers personalized mortgage solutions that align with your specific financial goals and circumstances. Our team of home mortgage professionals are standing by and ready to work closely with you to find the best mortgage options just for you.',
    ],
    disclosure_paragraphs: [],
  },
  encompass_lending_group: {
    title: 'Exploring what’s next or have questions on your loan?',
    card_name: 'The Mesa Homeowners Card for Encompass',
    partner_name: 'Encompass',
    description:
      "Your dedicated Encompass loan officer is here to help. Whether you're refinancing, purchasing, or just need guidance.",
    action_text: 'Speak to your Encompass Loan Officer',
    action_url: 'https://encompasslending.com/contact/',
    image_url: 'https://assets.mesamember.com/MesaMortgageDesigns/mesa-mortgage-encompass.png',
    details_paragraphs: [],
    disclosure_paragraphs: [],
  },
  lakeview: {
    title: 'Tap into your home equity to reach your goals.',
    card_name: 'The Mesa Homeowners Card for Lakeview',
    partner_name: 'Lakeview',
    description:
      'Pay off high-interest debt, make home improvements, fund large expenses like college tuition or medical bills - see what’s possible with your home equity. See terms.',
    action_text: 'Lakeview',
    action_url:
      'https://lakeview.com/equity-3/?utm_source=mesa&utm_medium=app&utm_campaign=LLS_launch&utm_term=v1',
    image_url: 'https://assets.mesamember.com/MesaMortgageDesigns/mesa-mortgage-lakeview.png',
    details_paragraphs: [],
    disclosure_paragraphs: [], // TODO(davy): Update for Lakeview
  },
  alcova: {
    title: 'Always Here, Whenever You Need Us!',
    card_name: 'The Mesa Homeowners Card for ALCOVA',
    partner_name: 'ALCOVA',
    description:
      'At ALCOVA, we’re more than your mortgage lender — we’re your mortgage partner for life. Whether it’s your next big move or just a quick question, our team is always here with personalized support to guide you forward.',
    action_text: 'Stay Connected',
    action_url: 'https://alcova.com/',
    image_url: 'https://assets.mesamember.com/MesaMortgageDesigns/mesa-mortgage-alcova.png',
    details_paragraphs: [
      'At ALCOVA, we’re more than your mortgage lender — we’re your mortgage partner for life. Whether it’s your next big move or just a quick question, our team is always here with personalized support to guide you forward.',
    ],
    disclosure_paragraphs: [],
  },
  carrington: {
    title: 'Home insurance made simple',
    card_name: 'The Mesa Homeowners Card for Carrington',
    partner_name: 'Carrington',
    description:
      'Compare 55+ trusted carriers and let us help you find savings in just a few clicks',
    action_text: 'Start My Quote',
    action_url:
      'https://getquote.itscovered.com/carrington?utm_campaign=27313757-Carrington-PartnerLinks-2025_11&utm_source=email&utm_medium=email&utm_content=Carrington-PartnerEmail-2025_11',
    image_url: 'https://assets.mesamember.com/MesaMortgageDesigns/mesa-mortgage-carrington.png',
    details_paragraphs: [
      'Carrington Mortgage Services, LLC and Covered Insurance teamed up to make it easy for you to find savings for all your insurance needs. With more than 25 insurance product offerings, let Covered’s team of agents help you find insurance savings on home, auto, pet, renters, and many more.',
    ],
    disclosure_paragraphs: [],
  },
} satisfies MortgageModuleDetailsSchema

export function useMortgageModuleDesign(variantName: MortgageModuleVariantName) {
  const mortgageModuleParameterStore = useMortgageModuleParameterStore()
  const logError = useLogError()
  const result = useMemo(() => {
    try {
      const moduleDetails = mortgageModuleParameterStore.get(
        'module_details',
        MODULE_FALLBACK_VALUES,
      )
      return MortgageModuleDetailsSchema.parse(moduleDetails)
    } catch (error) {
      return {
        error:
          error instanceof Error ? error : new Error('Failed to obtain parameter module_details'),
      } as const
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mortgageModuleParameterStore])

  useEffect(() => {
    if ('error' in result) {
      logError(
        new Error(`Failed to obtain module_details parameter ${variantName} with unknown error`, {
          cause: result,
        }),
      )
    }
  }, [logError, variantName, result])

  if (!(variantName in result)) {
    return { partner_code: 'default', ...MODULE_FALLBACK_VALUES['default'] } as const
  }

  if ('error' in result) {
    return { partner_code: variantName, ...MODULE_FALLBACK_VALUES[variantName] } as const
  }

  return { partner_code: variantName, ...result[variantName] }
}
